import {action, computed, makeAutoObservable, makeObservable, observable, runInAction, set, transaction} from "mobx";
import {interpret} from "xstate";
import {organizationStore} from "./StoreLoader";
import moment from "moment";
import {FetchMachine} from "../components/machines/FetchMachine";
import {getPlatform} from "../utils/StringUtilities";
import {sourceTypesEnum} from "../utils/SourceUtils";
import {isFacebookImageExpired} from "../utils/FacebookUtils";
import WatsonApi from "../backends/WatsonApi";
import {ExecutionEnvironment} from "./InterfaceStore";
import {isEvent, isFile, isMedia, isNews} from "../components/schoolfeed/ListItemGroup";

interface IReactiveContentItem extends ContentItem {
    update: (obj: { [v: string]: any }) => void,
    image: string,
}

export type IReactiveNewsContentItem = IReactiveContentItem & (NewsContentItem | MediaContentItem | FileContentItem);
export type IReactiveEventContentItem = IReactiveContentItem & EventContentItem;
export type IReactiveFileContentItem = IReactiveContentItem & FileContentItem;
export type SchoolFeedContentItem = IReactiveEventContentItem | IReactiveNewsContentItem | IReactiveFileContentItem;

export class ReactiveContentItem {
    // see https://github.com/mobxjs/mobx/issues/2486, should be able to remove this after upgrading to babel@7.12.0
    organization = undefined;
    item: NewsContentItem | MediaContentItem | FileContentItem | EventContentItem;

    constructor(item: NewsContentItem | MediaContentItem | FileContentItem | EventContentItem) {
        this.item = item;

        const obj = {
            update: action,
            image: computed,
        }
        for (const key in item) {
            if (item.hasOwnProperty(key)) {
                this[key] = item[key];
                obj[key] = observable;
            }
        }

        makeObservable(this, obj)
    }

    get image() {
        return getItemImage(this.item);
    }

    get altText() {
        return getAltText(this.item);
    }

    update(obj) {
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                this[key] = obj[key];
            }
        }
    }
}

function isNotEmpty(item) {
    return item.title || item.image || item.description;
}

export function getAltText(item: ContentItem) {
    const isImage = /photo/.test(item.type);
    let text;

    if (isNews(item) && item.json_data.news.cover_image_alt_text) {
        text = item.json_data.news.cover_image_alt_text;
    } else if (isEvent(item) && item.json_data.event?.full_picture) {
        text = item.json_data.event.full_picture_alt_text;
    } else if (isMedia(item) && isImage && item.json_data.images && item.json_data.images.length > 0) {
        text = item.json_data.images[item.json_data.images.length - 1].alt_text;
    } else if (isMedia(item) && item.json_data.videos) {
        text = item.json_data.videos.videos_list[0].alt_text;
    } else if (isFile(item) && item.json_data.file.cover_image_url) {
        text = item.json_data.file.cover_image_alt_text;
    }

    if (!text) {
        if (item.is_custom) {
            text = item.title;
        } else {
            text = `Image from ${getPlatform(item.type)}`;
        }
    }

    return text;
}

/**
 * @param item -
 * @param useSourceImageDefault - default to item's content_source image if item doesn't have its own image
 */
export function getItemImage(item: ContentItem, useSourceImageDefault: boolean = true) {
    const isImage = /photo/.test(item.type);
    const platform = getPlatform(item.type);

    let image: string | null  = null;

    // first try to get the image from the content item json_data
    if (isNews(item) && item.json_data.news.cover_image_url) {
        image = item.json_data.news.cover_image_url;
    } else if (isEvent(item) && item.json_data.event && item.json_data.event.full_picture) {
        image = item.json_data.event.full_picture;
    } else if (isMedia(item) && isImage && item.json_data.images && item.json_data.images.length > 0) {
        image = item.json_data.images[item.json_data.images.length - 1].raw_url;
    } else if (isMedia(item) && item.json_data.videos) {
        image = item.json_data.videos.videos_list[0].raw_url;
    } else if (isFile(item) && item.json_data.file.cover_image_url) {
        image = item.json_data.file.cover_image_url;
    }

    // make sure image URL isn't expired if it's from Facebook - if it is, we need to continue onward
    if (platform === sourceTypesEnum.FACEBOOK && isFacebookImageExpired(image)) {
        image = null;
    }

    // if we still don't have an image, try to extract from content source, making sure that if it's from Facebook it isn't expired
    if (
        !image &&
        useSourceImageDefault &&
        item.source &&
        item.source.account_image &&
        !(
            platform === sourceTypesEnum.FACEBOOK &&
            isFacebookImageExpired(item.source.account_image)
        ) &&
        platform !== sourceTypesEnum.GOOGLE_DRIVE
    ) {
        if (platform === sourceTypesEnum.TWITTER) {
            // use larger version of profile picture
            image = item.source.account_image.replace("_normal", "");
        } else {
            image = item.source.account_image;
        }
    }

    return image;
}

/*
switched from managing content in React state to a mobx store primarily because needed a way to easily observe and react to
item property changes - i.e. the published value. React doesn't provide a straightforward way to do that since object references
do not change. Also added benefit of being able to manage fetching and content from a single class instance rather than using
multiple states that need to be combined inside the component.
*/
export class SchoolFeedStore {
    // news contains is_news, is_media, and is_file.
    allContent: SchoolFeedContentItem[] = [];
    currentNewsPage = 0;
    currentEventsPage = 0;
    currentFilesPage = 0;
    isMoreNews = false;
    isMoreEvents = false;
    isMoreFiles = false;
    fetchStatus = FetchMachine.initialState.value;

    service = interpret(FetchMachine).onTransition(current => {
        runInAction(() => this.fetchStatus = current.value);
    });

    constructor() {
        makeAutoObservable(this);

        if (ExecutionEnvironment.canUseDOM) {
            // this leaks memory, so very important this doesn't get run on the server!
            this.service.start();
        }
    }

    getAnalytics = async () => {
        const client = await WatsonApi();
        const result = await client.apis.organizations.organizations_schoolfeed_items_analytics({
            organization_pk: organizationStore.organization.id,
        })
        if ((parseInt(result.obj.total.is_news) + parseInt(result.obj.total.is_file) + parseInt(result.obj.total.is_media)) > 12) {
            this.isMoreNews = true;
        }
        if (parseInt(result.obj.total.is_event) > 12) {
            this.isMoreEvents = true;
        }
    }

    get news(): IReactiveNewsContentItem[] {
        return this.allContent.filter(c => c.is_news) as IReactiveNewsContentItem[];
    }

    get events(): IReactiveEventContentItem[] {
        return this.allContent.filter(c => c.is_event) as IReactiveEventContentItem[];
    }

    get files(): IReactiveFileContentItem[] {
        return this.allContent.filter(c => c.is_file || c.is_folder) as IReactiveFileContentItem[];
    }

    _setNews = (items: NewsContentItem[] | SchoolFeedContentItem[]) => {
        items.forEach(item => {
            if (!this.news.find(n => {
                return n.source_id !== item.source_id &&
                    n.title === item.title &&
                    n.description === item.description
            })) {
                const contentItem: unknown = new ReactiveContentItem(item);
                if (isNotEmpty(contentItem)) {
                    this.allContent.push(contentItem as IReactiveNewsContentItem);
                }
            }
        })
    }

    _setEvents = (items: EventContentItem[]) => {
        items.forEach(item => {
            // filter out duplicates
            if (!this.events.find(n => {
                return n.source_id !== item.source_id &&
                    n.json_data.event.start === item.json_data.event.start &&
                    n.json_data.event.end === item.json_data.event.end &&
                    n.title === item.title &&
                    n.description === item.description
            })) {
                const contentItem: unknown = new ReactiveContentItem(item);
                if (isNotEmpty(contentItem)) {
                    this.allContent.push(contentItem as IReactiveEventContentItem);
                }
            }
        })
    }

    _setFiles = (items: FileContentItem[] | SchoolFeedContentItem[]) => {
        items.forEach(c => this.addFileItem(c));
    }

    setContentItems = (items: ContentItem[], clear = false) => {
        if (clear) {
            this.allContent = [];
        }
        items.forEach(c => {
            // @ts-ignore
            const contentItem: unknown = new ReactiveContentItem(c);
            this.allContent.push(contentItem as IReactiveNewsContentItem);
        })
    }

    fetchNews = async (baseFetchQuery) => {
        this.currentNewsPage++;
        const {send} = this.service;

        try {
            send("FETCH");
            const q = {
                ...baseFetchQuery,
                is_news: true,
                is_media: true,
                is_file: true,
                page_size: 10,
                expand: "organization",
                page: this.currentNewsPage,
            };
            const client = await WatsonApi();
            const obj = await client.apis["content-items"].content_items_list(q);
            const response = JSON.parse(obj.data);
            transaction(() => {
                // filter out duplicates
                this._setNews(response.data);
                this.isMoreNews = !!response.links.next;
            })
            send("FULFILL");
        } catch (e) {
            send("REJECT");
        }
    }

    fetchEvents = async (baseFetchQuery) => {
        this.currentEventsPage++;
        const {send} = this.service;

        try {
            send("FETCH");
            const q = {
                ...baseFetchQuery,
                is_event: true,
                page_size: 5,
                event_end: moment().format('YYYY-MM-DD'),
                ordering:"json_data__event__start",
                expand: "organization",
                page: this.currentEventsPage,
            };
            const client = await WatsonApi();
            const obj = await client.apis["content-items"].content_items_list(q);
            const response = JSON.parse(obj.data);
            transaction(() => {
                this._setEvents(response.data);
                this.isMoreEvents = !!response.links.next;
            })
            send("FULFILL");
        } catch (e) {
            send("REJECT");
        }
    }

    fetchMore = async (baseFetchQuery) => {
        if (this.isMoreNews) await this.fetchNews(baseFetchQuery);
        if (this.isMoreEvents) await this.fetchEvents(baseFetchQuery);
    }

    toggleHideItem = async (item, organization_id) => {
        const client = await WatsonApi();
        const result = await client.apis.organizations.organizations_content_items_partial_update({
            id: item.id,
            organization_pk: organization_id,
            data: {"user_published": !item.published},
        });
        if (item.is_news || item.is_media || item.is_file) {
            const found = this.news.find(c => c.id === item.id);
            if (found) {
                found.update(result.obj);
            }
        } else if (item.is_event) {
            const found = this.events.find(c => c.id === item.id);
            if (found) {
                found.update(result.obj);
            }
        }
    }

    deleteItem = async (item, organization_id) => {
        const client = await WatsonApi();
        await client.apis.organizations.organizations_content_items_delete({
            id: item.id,
            organization_pk: organization_id,
        });
        this.allContent = this.allContent.filter(n => n.id !== item.id);
    }

    addNewsItem = item => {
        const contentItem: unknown = new ReactiveContentItem(item);
        this.allContent.unshift(contentItem as IReactiveNewsContentItem);
    }

    addEventItem = item => {
        const contentItem: unknown = new ReactiveContentItem(item);
        this.allContent.unshift(contentItem as IReactiveEventContentItem);
    }

    addFileItem = item => {
        const contentItem: unknown = new ReactiveContentItem(item);
        this.allContent.unshift(contentItem as IReactiveFileContentItem);
    }

    updateItem = (updatedItem) => {
        const itemToUpdate = this.allContent.find(c => c.id === updatedItem.id);
        if (itemToUpdate) {
            set(itemToUpdate, updatedItem);
        }
    }

    get content() {
        const c: Array<IReactiveNewsContentItem | IReactiveEventContentItem> = [];
        let currentNewsIndex = 0;
        let currentEventsIndex = 0;

        const mixedNews = this.allContent.filter(c => !c.is_event);
        for (let i = 0; i < mixedNews.length + this.events.length; i++) {
            if ((i % 3 === 0 && currentEventsIndex < this.events.length) || currentNewsIndex === mixedNews.length) {
                c.push(this.events[currentEventsIndex] as IReactiveEventContentItem);
                currentEventsIndex++;
            } else if (currentNewsIndex < mixedNews.length) {
                c.push(mixedNews[currentNewsIndex] as IReactiveNewsContentItem);
                currentNewsIndex++;
            }
        }
        return c;
    }

    get isFetching(): boolean {
        return this.fetchStatus === "PENDING";
    }

    get isMore(): boolean {
        return this.isMoreNews || this.isMoreEvents;
    }
}
